import './preview-landing.css';

type Platform = 'desktop' | 'mobile';
type IntakeMode = 'first-run' | 'reimport';

const copy: Record<Platform, { kicker: string; title: string; body: string }> = {
  desktop: {
    kicker: 'Desktop setup',
    title: 'Choose your Fallout 2 folder and start.',
    body: 'Choose the folder, ZIP, or disc image. Rerun Reactor handles the rest.',
  },
  mobile: {
    kicker: 'Phone setup',
    title: 'Play Fallout 2 on your phone.',
    body: 'Load the archive from Files. The full game and every save stay in this phone browser.',
  },
};

const landing = document.querySelector<HTMLElement>('#screen-landing');
const wrap = landing?.querySelector<HTMLElement>(':scope > .wrap');
const template = document.querySelector<HTMLTemplateElement>('#preview-landing-template');

if (landing && wrap && template) {
  wrap.replaceChildren(template.content.cloneNode(true));
  landing.classList.add('preview-v2');

  const cards = Array.from(landing.querySelectorAll<HTMLButtonElement>('.preview-device-card'));
  const kicker = landing.querySelector<HTMLElement>('#preview-deck-kicker');
  const title = landing.querySelector<HTMLElement>('#preview-deck-title');
  const body = landing.querySelector<HTMLElement>('#preview-deck-body');
  const soundToggle = landing.querySelector<HTMLButtonElement>('[data-preview-sound]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let soundEnabled = false;

  const showPlatform = (platform: Platform) => {
    const selected = copy[platform];
    if (kicker) kicker.textContent = selected.kicker;
    if (title) title.textContent = selected.title;
    if (body) body.textContent = selected.body;
    for (const card of cards) {
      const active = card.dataset.platform === platform;
      card.classList.toggle('is-front', active);
      card.classList.toggle('is-back', !active);
      card.setAttribute('aria-pressed', String(active));
      const status = card.querySelector<HTMLElement>('.preview-deck-bar span:last-child');
      if (status) status.textContent = active ? 'NOW SHOWING' : 'SHOW DEMO';
      const video = card.querySelector<HTMLVideoElement>('video');
      if (!video) continue;
      video.muted = !(active && soundEnabled);
      if (active && !reducedMotion.matches) void video.play().catch(() => undefined);
      else video.pause();
    }
  };

  cards.forEach((card) => card.addEventListener('click', () => showPlatform(card.dataset.platform as Platform)));
  soundToggle?.addEventListener('click', () => {
    soundEnabled = !soundEnabled;
    soundToggle.textContent = soundEnabled ? 'Sound off' : 'Sound on';
    soundToggle.setAttribute('aria-pressed', String(soundEnabled));
    const activeVideo = cards.find((card) => card.classList.contains('is-front'))?.querySelector('video');
    cards.forEach((card) => {
      const video = card.querySelector('video');
      if (video) video.muted = video !== activeVideo || !soundEnabled;
    });
    if (activeVideo) void activeVideo.play().catch(() => undefined);
  });
  showPlatform('mobile');

  const intake = landing.querySelector<HTMLElement>('.preview-intake');
  const intakeKicker = intake?.querySelector<HTMLElement>('#preview-intake-kicker');
  const intakeTitle = intake?.querySelector<HTMLElement>('#preview-intake-title');
  const intakeCopy = intake?.querySelector<HTMLElement>('#preview-intake-copy');
  const intakeBuy = intake?.querySelector<HTMLElement>('[data-preview-intake-buy]');
  let lastFocus: HTMLElement | null = null;

  // The picker serves first-run setup and maintenance. Keep it outside the
  // landing screen so re-import can open over the launcher without navigating
  // the player back through the marketing page.
  if (intake) landing.closest('#app')?.append(intake);

  const configureIntake = (mode: IntakeMode) => {
    const reimporting = mode === 'reimport';
    if (intakeKicker) intakeKicker.textContent = reimporting ? 'Game data' : 'Play my copy';
    if (intakeTitle) intakeTitle.textContent = reimporting ? 'Replace game data.' : 'Load Fallout 2.';
    if (intakeCopy) {
      intakeCopy.textContent = reimporting
        ? 'Choose a new install folder, ZIP, or disc image. Your saves and save archive stay untouched.'
        : 'Choose an install folder, ZIP, or disc image. Your game files stay on your machine and are never uploaded.';
    }
    if (intakeBuy) intakeBuy.hidden = reimporting;
    intake?.querySelector('#import-progress')?.classList.remove('on');
    intake?.querySelector('#import-messages')?.replaceChildren();
  };

  const closeIntake = () => {
    if (!intake) return;
    intake.hidden = true;
    document.body.style.removeProperty('overflow');
    lastFocus?.focus();
    lastFocus = null;
  };
  const openIntake = (mode: IntakeMode = 'first-run') => {
    if (!intake) return;
    lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    configureIntake(mode);
    intake.hidden = false;
    document.body.style.overflow = 'hidden';
    intake.querySelector<HTMLElement>('.preview-intake-close')?.focus();
  };

  landing.querySelectorAll<HTMLElement>('[data-open-preview-intake]').forEach((button) =>
    button.addEventListener('click', () => openIntake('first-run'))
  );
  intake?.querySelectorAll<HTMLElement>('[data-close-preview-intake]').forEach((button) => button.addEventListener('click', closeIntake));
  window.addEventListener('rerun:open-intake', (event) => {
    const detail = (event as CustomEvent<{ mode?: IntakeMode }>).detail;
    openIntake(detail?.mode ?? 'first-run');
  });
  window.addEventListener('rerun:close-intake', closeIntake);
  intake?.addEventListener('click', (event) => {
    if (event.target === intake) closeIntake();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && intake && !intake.hidden) closeIntake();
  });

  const deck = landing.querySelector<HTMLElement>('.preview-deck-layout');
  if (deck && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) cards.forEach((card) => card.querySelector('video')?.pause());
      else {
        const active = cards.find((card) => card.classList.contains('is-front'));
        if (!reducedMotion.matches) void active?.querySelector('video')?.play().catch(() => undefined);
      }
    }, { threshold: 0.12 }).observe(deck);
  }
}
